import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { db, storage } from "./firebase";

const categoryToCollection = {
  'Appetizer': 'Appetizer',
  'entree': 'entree',
  'Rice And Bread': 'Rice And Bread',
  'Side': 'Side',
  'Salad': 'Salad',
  'dessert': 'dessert',
  'Beverages': 'Beverages'
};

const getImageUrl = async (imagePath) => {
  if (!imagePath) return null;
  
  try {
    const imageRef = ref(storage, imagePath);
    const url = await getDownloadURL(imageRef);
    return url;
  } catch (error) {
    console.error(`Error getting image URL for path ${imagePath}:`, error);
    return null;
  }
};

// Helper function to fetch dishes from a specific collection
const fetchDishesByCategory = async (collectionName, category, startId = 1) => {
  try {
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);
    const dishes = [];
    let idCounter = startId;
    
    for (const docSnapshot of snapshot.docs) {
      const firebaseData = docSnapshot.data();
      const dishName = firebaseData.name;
      
      if (!dishName) continue;
      
      // Get image URL if path is provided
      let imageUrl = null;
      if (firebaseData.image) {
        imageUrl = await getImageUrl(firebaseData.image);
      }
      
      const dish = {
        id: idCounter++,
        docId: docSnapshot.id, // Store Firebase document ID
        name: dishName,
        category: category,
        image: imageUrl,
        imagePath: firebaseData.image || null, // Store original image path
        description: firebaseData.description || ''
      };
      
      dishes.push(dish);
    }
    
    return dishes;
  } catch (error) {
    console.error(`Error fetching from collection ${collectionName}:`, error);
    return [];
  }
};

// Category-specific helper functions
export const fetchAppetizer = async () => {
  return await fetchDishesByCategory('Appetizer', 'Appetizer');
};

export const fetchEntree = async () => {
  return await fetchDishesByCategory('entree', 'entree');
};

export const fetchRiceAndBread = async () => {
  return await fetchDishesByCategory('Rice And Bread', 'Rice And Bread');
};

export const fetchSide = async () => {
  return await fetchDishesByCategory('Side', 'Side');
};

export const fetchSalad = async () => {
  return await fetchDishesByCategory('Salad', 'Salad');
};

export const fetchDessert = async () => {
  return await fetchDishesByCategory('dessert', 'dessert');
};

export const fetchBeverages = async () => {
  return await fetchDishesByCategory('Beverages', 'Beverages');
};

// Fetch all dishes from Firebase
export const fetchDishes = async () => {
  try {
    const [appetizers, entrees, riceAndBread, sides, salads, desserts, beverages] = await Promise.all([
      fetchAppetizer(),
      fetchEntree(),
      fetchRiceAndBread(),
      fetchSide(),
      fetchSalad(),
      fetchDessert(),
      fetchBeverages()
    ]);
    
    // Combine all dishes and reassign IDs sequentially
    const allDishes = [
      ...appetizers,
      ...entrees,
      ...riceAndBread,
      ...sides,
      ...salads,
      ...desserts,
      ...beverages
    ];
    
    // Reassign IDs sequentially
    return allDishes.map((dish, index) => ({
      ...dish,
      id: index + 1
    }));
  } catch (error) {
    console.error('Error fetching dishes from Firebase:', error);
    return [];
  }
};


// Helper function to get collection name from category
const getCollectionName = (category) => {
  return categoryToCollection[category] || category;
};

// Map category to folder name
const getCategoryFolder = (category) => {
  const categoryMap = {
    'Appetizer': 'appetizers',
    'entree': 'entrees',
    'Rice And Bread': 'rice-and-bread',
    'Side': 'sides',
    'Salad': 'salads',
    'dessert': 'desserts',
    'Beverages': 'beverages'
  };
  return categoryMap[category] || category.toLowerCase();
};

// Check if image already exists
const imageExists = async (imagePath) => {
  try {
    const imageRef = ref(storage, imagePath);
    await getDownloadURL(imageRef);
    return true; // Image exists
  } catch (error) {
    return false; // Image doesn't exist
  }
};

// Upload image to Firebase Storage and return the path
export const uploadDishImage = async (file, category, dishName) => {
  try {
    const categoryFolder = getCategoryFolder(category);
    // Use the original file name
    const originalFileName = file.name;
    const baseFileName = `images/${categoryFolder}/${originalFileName}`;
    
    // Check if image with same name already exists
    const exists = await imageExists(baseFileName);
    
    if (exists) {
      // If exists, return the existing path (don't upload duplicate)
      console.log(`Image already exists: ${baseFileName}, using existing image`);
      return baseFileName;
    }
    
    // Upload new image
    const storageRef = ref(storage, baseFileName);
    await uploadBytes(storageRef, file);
    return baseFileName;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Add a new dish to Firebase
export const addDish = async (dishData) => {
  try {
    const collectionName = getCollectionName(dishData.category);
    const collectionRef = collection(db, collectionName);
    
    const dishDoc = {
      name: dishData.name,
      description: dishData.description || '',
      image: dishData.image || null
    };
    
    const docRef = await addDoc(collectionRef, dishDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error adding dish:', error);
    throw error;
  }
};

export const updateDish = async (dishId, category, dishData) => {
  try {
    const collectionName = getCollectionName(category);
    const dishRef = doc(db, collectionName, dishId);
    
    const updateData = {};
    if (dishData.name !== undefined) updateData.name = dishData.name;
    if (dishData.description !== undefined) updateData.description = dishData.description;
    if (dishData.image !== undefined) updateData.image = dishData.image;
    
    await updateDoc(dishRef, updateData);
  } catch (error) {
    console.error('Error updating dish:', error);
    throw error;
  }
};

export const deleteDish = async (dishId, category) => {
  try {
    const collectionName = getCollectionName(category);
    const dishRef = doc(db, collectionName, dishId);
    await deleteDoc(dishRef);
  } catch (error) {
    console.error('Error deleting dish:', error);
    throw error;
  }
};

// Get dish document ID by name and category (helper for update/delete)
export const getDishDocId = async (dishName, category) => {
  try {
    const collectionName = getCollectionName(category);
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("name", "==", dishName));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].id;
    }
    return null;
  } catch (error) {
    console.error('Error getting dish document ID:', error);
    throw error;
  }
};

// Export empty array as default (App.js will call fetchDishes() to get Firebase data)
const defaultDishes = [];
export default defaultDishes;
